import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useLandingPageAnalyticsContext } from "@features/landing-page-analytics/hooks";
import { DATE_RANGE_CHOICE_LABELS } from "@features/sales-v3/top-level-filters/components/date-filters";
import {
  dateRangeOptionsForTimeGranularity,
  type DateRangeChoiceV3,
} from "@north-beam/nb-common";
import { FlatSelect, type FlatSelectOption } from "@shared/flat-select";
import { DateRangeCalendarSelect } from "@shared/selects/date-range-calendar-select";

type Props = {
  className?: string;
};

export const DateFilters = ({ className }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      level,
      dateRange,
      dateRangeInterval,
      breakdownFilters,
      adQuery,
      urlQuery,
      advancedSearch,
    },
    setDateRange,
    setPage,
  } = useLandingPageAnalyticsContext();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    level,
    dateRange,
    breakdownFilters,
    adQuery,
    urlQuery,
    advancedSearch,
  };

  const [selected, setSelected] = useState<FlatSelectOption<string>>({
    label: "",
    value: "",
  });

  useEffect(() => {
    if (typeof dateRange !== "string") {
      return setSelected({
        label: "",
        value: "custom",
      });
    }
    setSelected({
      label: "",
      value: dateRange,
    });
  }, [dateRange]);

  const options = useMemo(() => {
    return [
      {
        label: "",
        value: "custom",
        renderOption: ({ isSelected }) => (
          <DateRangeCalendarSelect
            calendarClassName="-ml-1 mt-1"
            value={dateRangeInterval}
            onApply={(v) => {
              prettyLogEventPage({
                domain: "Landing Page Analytics",
                action: "onChange",
                additionalActionInfo: "Set Date Range",
                data: {
                  newDateRange: v,
                  pageContext,
                },
              });

              setPage(1);
              setDateRange(v);
            }}
            buttonComponent={(ref, refProps) => (
              <div
                ref={ref}
                {...refProps}
                className={clsx("h-full flex items-center cursor-pointer", {
                  "text-white": isSelected,
                })}
              >
                Custom
              </div>
            )}
          />
        ),
      },
      ...dateRangeOptionsForTimeGranularity("daily").map(({ value }) => {
        return {
          label: DATE_RANGE_CHOICE_LABELS[value],
          value,
          onClick: () => {
            prettyLogEventPage({
              domain: "Landing Page Analytics",
              action: "onChange",
              additionalActionInfo: "Set Date Range",
              data: {
                newDateRange: value,
                pageContext,
              },
            });

            setPage(1);
            setDateRange(value as DateRangeChoiceV3);
          },
        };
      }),
    ] as FlatSelectOption<string>[];
  }, [dateRangeInterval, setDateRange]);

  return (
    <FlatSelect
      className={className}
      options={options}
      setSelected={(v) => {
        setSelected(v);
      }}
      selected={selected}
    />
  );
};
